import React, { useState, ReactNode, useEffect, useCallback } from "react";
import Head from "next/head";
import { getUrlForLocalImage, trackEvent } from "@lib/utils";
import BaseImage from "@components/BaseImage";
import { Card, Typography, Button } from "@kelp-dao/kernel-design-system";
import {
  CheckIcon,
  HandCoinsIcon,
  LockLaminatedOpenIcon,
  TrendUpIcon,
  CaretRightIcon,
  XIcon,
} from "@phosphor-icons/react";

// Types
type TModalState = "form" | "success" | "error";

type TPrivateVaultModalProps = {
  isVisible: boolean;
  isExpanded: boolean;
  onClose: () => void;
  onExpand: () => void;
  onSubmit?: (telegramId: string) => Promise<boolean>;
  isSubmitting?: boolean;
  /** Called when user closes modal after successful submission - hides everything including banner */
  onHideCompletely?: () => void;
};

type TFeatureRowProps = {
  icon: ReactNode;
  title: string;
  value: string;
  width?: number;
};

type TSkewedRectDecorationProps = {
  className?: string;
};

type TFloatingBannerProps = {
  onClick: () => void;
};

type TSuccessContentProps = {
  onClose: () => void;
};

type TErrorContentProps = {
  onRetry: () => void;
};

type TFormContentProps = {
  telegramId: string;
  setTelegramId: (value: string) => void;
  handleConnect: () => void;
  isSubmitting?: boolean;
};

type TExpandedPopupProps = {
  onClose: () => void;
  telegramId: string;
  setTelegramId: (value: string) => void;
  handleConnect: () => void;
  isSubmitting?: boolean;
  modalState: TModalState;
  onRetry: () => void;
};

// Analytics events
const PRIVATE_VAULT_BANNER_CLICKED = "private_vault_banner_clicked";
const PRIVATE_VAULT_MODAL_CLOSED = "private_vault_modal_closed";
const PRIVATE_VAULT_CONNECT_CLICKED = "private_vault_connect_clicked";

// Feature Row Component for expanded popup
const FeatureRow = ({ icon, title, value, width = 50 }: TFeatureRowProps) => (
  <Card
    variant="non-bordered"
    className="relative bg-bg-card border border-border-default rounded-lg p-3"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1">
        <div className="text-accent-highlight1">{icon}</div>
        <Typography
          as="span"
          type="body"
          variant="xsmall"
          className="font-normal text-text-primary"
        >
          {title}
        </Typography>
      </div>
      <Typography
        as="span"
        type="body"
        variant="xsmall"
        className="font-clash font-medium text-feedback-success"
      >
        {value}
      </Typography>
    </div>
    {/* Blur highlight effect */}
    <div
      className="absolute right-3 top-[11px] h-[18px] bg-[#C4FFE833] backdrop-blur-[3px]"
      style={{ width: `${width}px` }}
    />
  </Card>
);

// Reusable skewed rectangle decoration
const SkewedRectDecoration = ({ className }: TSkewedRectDecorationProps) => (
  <BaseImage
    src="/assets/rectangle-skewed.svg"
    alt=""
    width={20}
    height={20}
    className={className}
  />
);

// Floating Banner Component (collapsed state)
const FloatingBanner = ({ onClick }: TFloatingBannerProps) => (
  <button
    onClick={onClick}
    className="group relative flex items-center gap-1 md:gap-3 px-1 md:px-4 py-3 w-full md:w-auto md:rounded-xl overflow-hidden cursor-pointer transition-transform md:hover:scale-[1.02] active:scale-[0.98]"
    style={{
      backgroundImage: `url(${getUrlForLocalImage(
        "/assets/vaults/private-vault-bg.svg"
      )})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }}
  >
    {/* Pink diagonal stripes on right - desktop only */}
    {/* <div className="hidden md:block absolute right-0 top-0 bottom-0 w-3 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background:
            "repeating-linear-gradient(135deg, transparent, transparent 3px, #ff9ec4 3px, #ff9ec4 6px)",
        }}
      />
    </div> */}

    {/* Treasure chest icon */}
    <div className="relative flex-shrink-0">
      <BaseImage
        src="/assets/vaults/private-vault-icon.svg"
        alt="Private Vault"
        width={75}
        height={75}
        className="object-contain w-[35px] h-[35px] md:w-[75px] md:h-[75px]"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.style.display = "none";
          target.parentElement!.innerHTML = '<span class="text-2xl">🎁</span>';
        }}
      />
      {/* Sparkles */}
      <div className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
      <div className="absolute top-0 right-2 w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse delay-100" />
    </div>

    {/* Text content */}
    <div className="flex md:flex-col items-start gap-1 flex-1 md:flex-none md:pr-4">
      <Typography
        as="span"
        type="heading"
        variant="xxsmall"
        className="font-clash font-medium text-sm leading-tight text-text-primary"
      >
        Unlock private access
      </Typography>
      <Typography
        as="span"
        type="body"
        variant="xsmall"
        className="inline-flex px-1 py-0.5 rounded-[3.5px] text-[10px] md:text-[12px] font-regular bg-[#F5B64C99] text-text-primary"
      >
        Higher rewards & custom terms
      </Typography>
    </div>
    <CaretRightIcon
      size={16}
      className=" text-text-secondary group-hover:translate-x-0.5 transition-transform"
    />
  </button>
);

// Success State Content
const SuccessContent = ({ onClose }: TSuccessContentProps) => (
  <div className="flex flex-col items-center text-center gap-4 py-6">
    <div className="w-16 h-16 rounded-full bg-feedback-success flex items-center justify-center">
      <CheckIcon size={32} className="text-white" strokeWidth={3} />
    </div>
    <div className="flex flex-col gap-2">
      <Typography
        as="h2"
        type="heading"
        variant="xsmall"
        className="font-clash font-medium text-lg leading-[1.3] text-text-primary"
      >
        Request received
      </Typography>
      <Typography
        as="p"
        type="body"
        variant="xsmall"
        className="font-normal leading-[1.5] text-text-secondary max-w-[220px]"
      >
        Our team will review your eligibility and reach out shortly.
      </Typography>
    </div>
    <Button
      onClick={onClose}
      className="px-4 py-2 rounded-lg text-xs font-normal"
    >
      Got it
    </Button>
  </div>
);

// Error State Content
const ErrorContent = ({ onRetry }: TErrorContentProps) => (
  <div className="flex flex-col items-center text-center gap-4 py-6">
    <div className="w-16 h-16 rounded-full bg-feedback-error flex items-center justify-center">
      <XIcon size={32} className="text-white" strokeWidth={3} />
    </div>
    <div className="flex flex-col gap-2">
      <Typography
        as="h2"
        type="heading"
        variant="xsmall"
        className="font-clash font-medium text-lg leading-[1.3] text-text-primary"
      >
        Something went wrong
      </Typography>
      <Typography
        as="p"
        type="body"
        variant="xsmall"
        className="font-normal leading-[1.5] text-text-secondary max-w-[220px]"
      >
        Couldn&apos;t submit your request. Please try again.
      </Typography>
    </div>
    <Button
      onClick={onRetry}
      className="px-4 py-2 rounded-lg text-xs font-normal"
    >
      Try again
    </Button>
  </div>
);

// Form Content for expanded popup
const FormContent = ({
  telegramId,
  setTelegramId,
  handleConnect,
  isSubmitting,
}: TFormContentProps) => (
  <div className="flex flex-col gap-6 w-full">
    {/* Description */}
    <Typography
      as="p"
      type="body"
      variant="xsmall"
      className="font-normal leading-[1.4] text-text-secondary text-center"
    >
      You&apos;re eligible for exclusive rewards <br /> and custom vault terms.
    </Typography>

    {/* Feature Rows */}
    <div className="flex flex-col gap-3 relative">
      <FeatureRow
        icon={<HandCoinsIcon size={18} />}
        title="Minimal fees till"
        value="upto 2%"
      />
      <SkewedRectDecoration className="absolute right-2 -top-[10px]" />

      <FeatureRow
        icon={<TrendUpIcon size={18} />}
        title="Higher rewards up to"
        value="upto6%"
        width={59}
      />
      <SkewedRectDecoration className="absolute right-10 top-[46px]" />

      <FeatureRow
        icon={<LockLaminatedOpenIcon size={18} />}
        title="Private access"
        value="Invite-only"
        width={65}
      />
      <SkewedRectDecoration className="absolute right-2 bottom-[34px]" />
    </div>

    {/* Telegram Input Section */}
    <div className="flex flex-col gap-0 w-full">
      <Typography
        as="span"
        type="body"
        variant="xsmall"
        className="font-normal leading-[1.4] text-text-primary block"
      >
        Enter your telegram ID to connect with us:
      </Typography>
      {/* <Typography
        as="span"
        type="body"
        variant="xsmall"
        className="font-regular text-text-secondary"
      >
        Connect to learn more and activate access.
      </Typography> */}
      <div className="py-1 relative flex items-center bg-bg-primary rounded-lg border border-border-default mt-1">
        <input
          type="text"
          placeholder="@username"
          value={telegramId}
          onChange={(e) => setTelegramId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && telegramId.trim() && !isSubmitting) {
              handleConnect();
            }
          }}
          className="w-full bg-transparent pl-4 pr-[110px] py-2 text-xs text-text-primary placeholder:text-text-secondary rounded-lg border-none outline-none focus:ring-0"
        />
        <div className="absolute right-2">
          <Button
            onClick={handleConnect}
            disabled={!telegramId.trim() || isSubmitting}
            className="flex items-center gap-1 px-2 py-1.5 rounded text-xs font-normal leading-[1.3]"
          >
            {isSubmitting ? "Submitting..." : "Connect now"}
          </Button>
        </div>
      </div>
    </div>
  </div>
);

// Expanded Popup Component - Desktop floating, Mobile bottom sheet
const ExpandedPopup = ({
  onClose,
  telegramId,
  setTelegramId,
  handleConnect,
  isSubmitting,
  modalState,
  onRetry,
}: TExpandedPopupProps) => (
  <Card
    variant="non-bordered"
    className="w-full md:w-[336px] rounded-t-2xl rounded-b-none md:rounded-2xl overflow-hidden p-0"
    style={{
      boxShadow:
        "0px 8px 18px 0px rgba(0,0,0,0.1), 0px 33px 33px 0px rgba(0,0,0,0.09), 0px 74px 44px 0px rgba(0,0,0,0.05), 0px 132px 53px 0px rgba(0,0,0,0.01)",
    }}
  >
    {/* Header */}
    <div
      className="relative h-[140px] md:h-[160px] overflow-hidden flex items-center justify-center pb-[16px] px-[16px]"
      style={{
        backgroundImage: `url(${getUrlForLocalImage(
          "/assets/vaults/private-vault-bg.svg"
        )})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-2 right-2 p-2 bg-bg-card border border-border-default rounded-lg text-subbrand-primary hover:bg-button-secondary-hover transition-colors z-10"
        aria-label="Close"
      >
        <XIcon size={16} />
      </button>

      {/* Header text */}
      <div className="flex flex-col items-center text-center gap-0.5">
        <Typography
          as="span"
          type="body"
          variant="small"
          className="font-clash font-medium text-base leading-[1.2] text-brand-primary"
        >
          Unlock privileges
        </Typography>
        <Typography
          as="span"
          type="heading"
          variant="xsmall"
          className="font-clash font-medium text-xl leading-[1.35] text-text-primary"
        >
          Private vault access
        </Typography>
      </div>
    </div>

    {/* Content */}
    <div className="bg-bg-card rounded-t-2xl -mt-8 relative px-5 py-6 pb-8 overflow-hidden">
      {modalState === "form" && (
        <FormContent
          telegramId={telegramId}
          setTelegramId={setTelegramId}
          handleConnect={handleConnect}
          isSubmitting={isSubmitting}
        />
      )}
      {modalState === "success" && <SuccessContent onClose={onClose} />}
      {modalState === "error" && <ErrorContent onRetry={onRetry} />}
    </div>
  </Card>
);

const PrivateVaultModal = ({
  isVisible,
  isExpanded,
  onClose,
  onExpand,
  onSubmit,
  isSubmitting = false,
  onHideCompletely,
}: TPrivateVaultModalProps) => {
  const [telegramId, setTelegramId] = useState("");
  const [modalState, setModalState] = useState<TModalState>("form");

  // Reset state when collapsed
  useEffect(() => {
    if (!isExpanded) {
      const timer = setTimeout(() => {
        setModalState("form");
        setTelegramId("");
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  // Prevent body scroll when expanded on mobile
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isExpanded]);

  const handleBannerClick = () => {
    trackEvent(PRIVATE_VAULT_BANNER_CLICKED, {});
    onExpand();
  };

  const handleClose = useCallback(() => {
    trackEvent(PRIVATE_VAULT_MODAL_CLOSED, {
      telegram_id_entered: !!telegramId.trim(),
      modal_state: modalState,
    });

    // If closing from success state, hide everything including banner
    if (modalState === "success" && onHideCompletely) {
      onHideCompletely();
    } else {
      onClose();
    }
  }, [telegramId, modalState, onClose, onHideCompletely]);

  // Handle ESC key to close modal (only in form state)
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && isExpanded && modalState === "form") {
        handleClose();
      }
    },
    [isExpanded, modalState, handleClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleConnect = async () => {
    if (telegramId.trim() && !isSubmitting) {
      trackEvent(PRIVATE_VAULT_CONNECT_CLICKED, {
        telegram_id: telegramId,
      });

      if (onSubmit) {
        const success = await onSubmit(telegramId);
        setModalState(success ? "success" : "error");
      } else {
        setModalState("success");
      }
    }
  };

  const handleRetry = () => {
    setModalState("form");
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Prefetch images for removing delay in load */}
      <Head>
        <link
          rel="prefetch"
          href={getUrlForLocalImage("/assets/vaults/private-vault-bg.svg")}
          as="image"
        />
        <link
          rel="prefetch"
          href={getUrlForLocalImage("/assets/vaults/private-vault-icon.svg")}
          as="image"
        />
      </Head>

      {/* Collapsed State - Floating Banner */}
      <div
        className={`fixed z-[1000] transition-all duration-300 bottom-0 left-0 right-0 md:bottom-6 md:right-6 md:left-auto ${
          isExpanded
            ? "opacity-0 scale-95 pointer-events-none"
            : "opacity-100 scale-100"
        }`}
      >
        <FloatingBanner onClick={handleBannerClick} />
      </div>

      {/* Expanded State - Mobile: Bottom Sheet with Overlay | Desktop: Floating Popup */}
      {isExpanded && (
        <>
          {/* Overlay - only closeable when in form state, not on success/error */}
          <div
            className="fixed inset-0 bg-black/30 z-[1000] transition-opacity duration-300"
            onClick={modalState === "form" ? handleClose : undefined}
          />

          {/* Popup/Bottom Sheet */}
          <div
            className={`fixed z-[1001] transition-all duration-300 bottom-0 left-0 right-0 md:bottom-6 md:right-6 md:left-auto md:w-auto ${
              isExpanded
                ? "translate-y-0 opacity-100"
                : "translate-y-full md:translate-y-0 opacity-0"
            }`}
          >
            <ExpandedPopup
              onClose={handleClose}
              telegramId={telegramId}
              setTelegramId={setTelegramId}
              handleConnect={handleConnect}
              isSubmitting={isSubmitting}
              modalState={modalState}
              onRetry={handleRetry}
            />
          </div>
        </>
      )}
    </>
  );
};

export default PrivateVaultModal;
